import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { extend, reverse, sortBy, groupBy } from 'lodash';

import withViewportWidth from 'decorators/withViewportWidth';

import { GoogleMap } from 'components/core/library/mapV2';
import { ZoomControls } from 'components/core/Map';
import GoogleMaps from 'lib/googleMaps';
import { isLarge } from 'utils/breakpoints';
import { locationsToBounds } from 'utils/locationsToBounds';
import toLatLng from 'utils/toLatLng';

import FacilityMarkers from './FacilityMarkers';
import SearchOnMoveControl from './SearchOnMoveControl';

export function withGoogleOnMount(WrappedComponent) {
	function WithGoogleOnMount(props) {
		const [google, setGoogle] = React.useState(null);

		React.useEffect(() => {
			const { google: googlePromise } = GoogleMaps.getInstance({
				key: props.googleMapsKey,
			});
			googlePromise.then(setGoogle);
		}, []);

		if (!google) return null;

		return (
			<WrappedComponent
				{...props}
				google={google}
			/>
		);
	}
	return WithGoogleOnMount;
}

function withMapFacilitiesLogic(WrappedComponent) {
	function WithMapFacilitiesLogic(props) {
		const facilities = React.useMemo(() => {
			let facs = props.facilities.map((facility) =>
				extend(facility, { location: toLatLng(facility.location) }),
			);

			facs = groupBy(
				reverse(
					sortBy(
						facs.filter((f) => f.inNetwork),
						'location.lat',
					),
				),
				(f) => f.isSelfStorage && 'storage',
			);

			return facs.storage || [];
		}, [props.facilities]);

		const [currentZoom, setCurrentZoom] = React.useState(props.initialZoom);
		const [loading, setLoading] = React.useState(true);
		const [showRedoButton, setShowRedoButton] = React.useState(false);
		const [searchOnMoveState, setSearchOnMoveState] = React.useState(
			props.searchOnMove,
		);
		const [mapBounds, setMapBounds] = React.useState(null);

		const zoomIn = () => setCurrentZoom((z) => z + 1);
		const zoomOut = () => setCurrentZoom((z) => z - 1);
		const handleZoomChange = (zoom) => setCurrentZoom(zoom);
		const handleIdle = () => {
			props.onReady();
			setLoading(false);
		};
		const toggleSearchOnMove = () => setSearchOnMoveState((s) => !s);
		const handleRedoSearch = () => {
			props.onRedoSearch(mapBounds);
			setShowRedoButton(false);
			setSearchOnMoveState(true);
		};

		const boundsAndCenter = React.useMemo(() => {
			const { google, searchLocation, preselectedFacilityId } = props;
			const preselectedFacility = facilities.filter(
				({ id }) => id === preselectedFacilityId,
			);
			if (google && facilities.length) {
				// Map Polygons if only has FSSd
				const initialBounds = locationsToBounds(
					google,
					preselectedFacility.length
						? preselectedFacility
						: facilities,
				);
				// If I have a pre-selected facility it will center on it
				// If not I will center within the collection of facilities
				const centerObj =
					preselectedFacilityId && preselectedFacility.length
						? locationsToBounds(
								google,
								preselectedFacility,
							).getCenter()
						: initialBounds.getCenter();
				const center = { lat: centerObj.lat(), lng: centerObj.lng() };

				return { initialBounds, center };
			}
			return { center: searchLocation };
		}, [props.google, facilities, props.searchLocation]);

		// Override search on move on smaller screens
		const searchOnMove = React.useMemo(
			() => (isLarge(props.viewportWidth) ? searchOnMoveState : false),
			[props.breakpoint, searchOnMoveState],
		);

		const handleBoundsChange = (bounds) => {
			if (searchOnMove) {
				props.onRedoSearch(bounds);
			}

			setMapBounds(bounds);
			setShowRedoButton(!searchOnMove);
		};

		return (
			<WrappedComponent
				{...props}
				facilities={facilities}
				currentZoom={currentZoom}
				loading={loading}
				showRedoButton={showRedoButton}
				searchOnMove={searchOnMove}
				mapBounds={mapBounds}
				zoomIn={zoomIn}
				zoomOut={zoomOut}
				handleZoomChange={handleZoomChange}
				handleIdle={handleIdle}
				toggleSearchOnMove={toggleSearchOnMove}
				handleRedoSearch={handleRedoSearch}
				initialBounds={boundsAndCenter.initialBounds}
				center={boundsAndCenter.center}
				handleBoundsChange={handleBoundsChange}
			/>
		);
	}
	return WithMapFacilitiesLogic;
}

export const withMapFacilities = (Component) =>
	withViewportWidth(withMapFacilitiesLogic(Component));

export function Map({
	google, center, currentZoom, handleZoomChange, handleBoundsChange, handleIdle, facilities, breakpoint,
	loading, zoomIn, zoomOut, showRedoButton, searchOnMove, toggleSearchOnMove, handleRedoSearch, initialBounds,
	phone, highlightedFacility, getLocalPhoneNumberByZipCode,
	preselectedFacilityId, showControls = true
}) {
	return (
		<div className="map-container-v2">
			<GoogleMap
				center={center}
				initialBounds={initialBounds}
				zoom={currentZoom}
				onZoomChanged={handleZoomChange}
				onBoundsChange={handleBoundsChange}
				onIdle={handleIdle}
				google={google}
				preselectedFacilityId={preselectedFacilityId}
			>
				<FacilityMarkers
					facilities={facilities}
					highlightedFacility={highlightedFacility}
					breakpoint={breakpoint}
					phone={phone}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
					preselectedFacilityId={preselectedFacilityId}
				/>
			</GoogleMap>
			{showControls && <div className={classnames('map-control-container-v2', { loading })}>
				<div className="search-on-move-box">
					<SearchOnMoveControl
						showRedoButton={showRedoButton}
						searchOnMove={searchOnMove}
						onToggleSearchOnMove={toggleSearchOnMove}
						onRedoSearch={handleRedoSearch}
					/>
				</div>
				<ZoomControls
					onZoomIn={zoomIn}
					onZoomOut={zoomOut}
				/>
			</div>}
		</div>
	);
}

Map.propTypes = {
	zoomIn: PropTypes.func,
	zoomOut: PropTypes.func,
	handleZoomChange: PropTypes.func,
	handleBoundsChange: PropTypes.func,
	center: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number
	}),
	currentZoom: PropTypes.number,
	handleIdle: PropTypes.func,
	facilities: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	showRedoButton: PropTypes.bool,
	searchOnMove: PropTypes.bool,
	toggleSearchOnMove: PropTypes.func,
	handleRedoSearch: PropTypes.func,
	initialBounds: PropTypes.object,
	google: PropTypes.object,
	breakpoint: PropTypes.string,
	phone: PropTypes.object,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	highlightedFacility: PropTypes.number,
	preselectedFacilityId: PropTypes.number
};

export const enhance = (Component) =>
	withGoogleOnMount(withMapFacilities(Component));

export default enhance(Map);
